import { motion } from "framer-motion";
import { Star, Quote, BadgeCheck } from "lucide-react";
import { useT } from "@/lib/i18n";

/**
 * Each testimonial has:
 *   q, name, from (localized), initials, flag emoji, accent class for avatar
 */
const ITEMS = [
  {
    id: 1,
    qKey: "tst.1.q",
    nameKey: "tst.1.name",
    fromKey: "tst.1.from",
    initials: "AK",
    flag: "🇹🇷",
    accent: "from-rose-500/30 to-gold/30",
  },
  {
    id: 2,
    qKey: "tst.2.q",
    nameKey: "tst.2.name",
    fromKey: "tst.2.from",
    initials: "RM",
    flag: "🇦🇿",
    accent: "from-indigo-500/30 to-gold/30",
  },
  {
    id: 3,
    qKey: "tst.3.q",
    nameKey: "tst.3.name",
    fromKey: "tst.3.from",
    initials: "EP",
    flag: "🇬🇧",
    accent: "from-emerald-500/30 to-gold/30",
  },
  {
    id: 4,
    qKey: "tst.4.q",
    nameKey: "tst.4.name",
    fromKey: "tst.4.from",
    initials: "RA",
    flag: "🇦🇿",
    accent: "from-sky-500/30 to-gold/30",
  },
];

export default function Testimonials() {
  const { t } = useT();

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 bg-navy-800/40"
    >
      <div className="absolute inset-x-0 top-0 divider-gold" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.3em] text-gold uppercase mb-4">
              {t("tst.eyebrow")}
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
              {t("tst.title.1")}{" "}
              <span className="italic text-gold">{t("tst.title.2")}</span>
            </h2>
          </div>

          {/* Aggregate rating card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="rating-card"
            className="shrink-0 bg-navy-900/70 border border-gold/30 rounded-2xl px-6 py-5 min-w-[260px]"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex">
                {[...Array(5)].map((_, idx) => (
                  <Star key={idx} className="h-4 w-4 fill-gold text-gold" />
                ))}
              </div>
              <div className="font-serif text-2xl text-white">
                {t("tst.rating.score")}
              </div>
            </div>
            <div className="text-xs text-slate-400 leading-relaxed">
              {t("tst.rating.sub")}
            </div>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {ITEMS.map((tt, i) => (
            <motion.figure
              key={tt.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`testimonial-${i}`}
              className="relative bg-navy-900/70 border border-gold/20 rounded-3xl p-7 hover:border-gold/60 transition-[color,background-color,border-color,box-shadow,transform] flex flex-col"
            >
              <Quote className="absolute top-5 right-5 h-7 w-7 text-gold/20" />

              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, idx) => (
                  <Star key={idx} className="h-3.5 w-3.5 fill-gold text-gold" />
                ))}
              </div>

              <blockquote className="text-slate-200 leading-relaxed font-serif text-base italic flex-1">
                “{t(tt.qKey)}”
              </blockquote>

              <figcaption className="mt-6 pt-5 border-t border-gold/15 flex items-center gap-3">
                <div
                  className={`relative h-11 w-11 shrink-0 rounded-full bg-gradient-to-br ${tt.accent} border border-gold/30 flex items-center justify-center`}
                >
                  <span className="font-serif text-sm text-white font-semibold">
                    {tt.initials}
                  </span>
                  <span className="absolute -bottom-0.5 -right-0.5 text-sm leading-none">
                    {tt.flag}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-white truncate">
                    {t(tt.nameKey)}
                  </div>
                  <div className="text-[11px] text-gold mt-0.5 tracking-wider uppercase truncate">
                    {t(tt.fromKey)}
                  </div>
                </div>
              </figcaption>

              <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] text-emerald-300/80 tracking-wider uppercase">
                <BadgeCheck className="h-3 w-3" />
                {t("tst.verified")}
              </div>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
